import { supabase } from '@/integrations/supabase/client';

const PROD_HOSTS = ['app.munilex.es', 'munilex.es', 'opos.munilex.es', 'www.munilex.es'];
const isProduction = PROD_HOSTS.some(h => window.location.hostname === h) || window.location.hostname.includes('lovable.app');
export const API_URL = import.meta.env.VITE_API_URL || (isProduction ? 'https://app.munilex.es/api' : 'http://localhost:3100/api');

function getEdgeFunctionUrl(functionName: string): string {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    return `${supabaseUrl}/functions/v1/${functionName}`;
}

/**
 * Get a valid access token, proactively refreshing if it expires within 60 seconds.
 */
async function getValidAccessToken(): Promise<string | null> {
    let { data: { session } } = await supabase.auth.getSession();

    if (!session) return null;

    const expiresAt = session.expires_at;
    const now = Math.floor(Date.now() / 1000);
    const isExpiringSoon = expiresAt && (expiresAt - now) < 60;

    if (isExpiringSoon) {
        console.log('[apiClient] Token expiring soon, refreshing...');
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshError || !refreshData.session) {
            console.error('[apiClient] Failed to refresh session:', refreshError);
            return null;
        }

        session = refreshData.session;
        console.log('[apiClient] Token refreshed successfully');
    }

    return session.access_token;
}

/**
 * Replaces `supabase.functions.invoke`.
 * Automatically gets the current session token and attaches it.
 * Proactively refreshes tokens about to expire.
 * Retries once on 401 with a fresh token.
 * Falls back to Supabase edge functions if VPS is unreachable.
 */
export async function invokeApi<T>(
    functionName: string,
    options: { body?: Record<string, any>; headers?: Record<string, string>; timeoutMs?: number } = {}
): Promise<{ data: T | null; error: Error | null }> {
    try {
        const t0 = performance.now();
        let accessToken = await getValidAccessToken();

        const buildHeaders = (token: string | null): Record<string, string> => {
            const h: Record<string, string> = {
                'Content-Type': 'application/json',
                ...(options.headers || {}),
            };
            if (token) h['Authorization'] = `Bearer ${token}`;
            return h;
        };

        let response: Response;
        let usedFallback = false;

        // Dynamic timeout: 75s for heavy AI generation tasks, 45s for standard queries
        const isHeavyTask = functionName.includes('generate') || functionName.includes('study-plan') || functionName.includes('resumen');
        const timeoutMs = options.timeoutMs ?? (isHeavyTask ? 75000 : 45000);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        try {
            response = await fetch(`${API_URL}/${functionName}`, {
                method: 'POST',
                headers: buildHeaders(accessToken),
                body: options.body ? JSON.stringify(options.body) : undefined,
                signal: controller.signal,
            });

            // Retry once on 401 with refreshed token
            if (response.status === 401) {
                console.log(`[apiClient] 401 on ${functionName}, refreshing token and retrying...`);
                const { data: refreshData } = await supabase.auth.refreshSession();
                if (refreshData.session) {
                    accessToken = refreshData.session.access_token;
                    response = await fetch(`${API_URL}/${functionName}`, {
                        method: 'POST',
                        headers: buildHeaders(accessToken),
                        body: options.body ? JSON.stringify(options.body) : undefined,
                        signal: controller.signal,
                    });
                }
            }

                        // Fallback en cualquier respuesta no-OK salvo 401/403 (auth real) ya gestionados arriba
            if (!response.ok && response.status !== 401 && response.status !== 403) {
                throw new Error(`VPS error (${response.status})`);
            }

            // Check for HTML response or invalid JSON structure
            if (response.ok) {
                const contentType = response.headers.get('content-type') || '';
                if (contentType.includes('text/html')) {
                    throw new Error('VPS returned HTML instead of JSON');
                }
                
                try {
                    const clonedResponse = response.clone();
                    const json = await clonedResponse.json();
                    
                    // Verify structure for explain calls
                    if (options.body && options.body.mode === 'explain') {
                        if (!json || json.explanation === undefined) {
                            throw new Error('VPS JSON shape mismatch: missing explanation field');
                        }
                    }
                } catch (jsonErr) {
                    throw new Error('VPS returned invalid or unexpected JSON structure: ' + jsonErr.message);
                }
            }

            clearTimeout(timeoutId);
        } catch (primaryError: any) {
            clearTimeout(timeoutId);
            if (primaryError?.name === 'AbortError') {
                console.warn(`[apiClient] VPS timeout (40s) on ${functionName}, falling back to edge function`);
            }
            usedFallback = true;
            console.warn(`VPS unreachable for ${functionName}, falling back to edge function:`, primaryError);
            const fallbackHeaders = {
                ...buildHeaders(accessToken),
                'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            };
            response = await fetch(getEdgeFunctionUrl(functionName), {
                method: 'POST',
                headers: fallbackHeaders,
                body: options.body ? JSON.stringify(options.body) : undefined,
            });
        }

        const tResponse = performance.now();
        console.log(`⏱️ [API] ${functionName}: ${(tResponse - t0).toFixed(0)}ms ${usedFallback ? '(FALLBACK)' : '(VPS)'} → ${response.status}`);

        if (!response.ok) {
            const text = await response.text();
            let errorMsg = `Error calling API (${response.status}): ${text}`;
            try {
                const errorJson = JSON.parse(text);
                if (errorJson.error) errorMsg = errorJson.error;
            } catch (e) {
                // Ignorar error de parsing
            }
            throw new Error(errorMsg);
        }

        const responseData = await response.json();
        return { data: responseData as T, error: null };
    } catch (err: any) {
        console.error(`Error en invokeApi(${functionName}):`, err);
        return { data: null, error: err instanceof Error ? err : new Error(String(err)) };
    }
}

/**
 * Same as invokeApi but does NOT attempt fallback to edge functions.
 * Used for VPS-only endpoints like upload-to-workspace.
 * Includes proactive token refresh and 401 retry.
 */
export async function invokeApiRaw<T>(
    functionName: string,
    options: { body?: Record<string, any>; headers?: Record<string, string> } = {}
): Promise<{ data: T | null; error: Error | null }> {
    try {
        let accessToken = await getValidAccessToken();

        const buildHeaders = (token: string | null): Record<string, string> => {
            const h: Record<string, string> = {
                'Content-Type': 'application/json',
                ...(options.headers || {}),
            };
            if (token) h['Authorization'] = `Bearer ${token}`;
            return h;
        };

        let response = await fetch(`${API_URL}/${functionName}`, {
            method: 'POST',
            headers: buildHeaders(accessToken),
            body: options.body ? JSON.stringify(options.body) : undefined,
        });

        // Retry once on 401 with refreshed token
        if (response.status === 401) {
            console.log(`[apiClient] 401 on ${functionName}, refreshing token and retrying...`);
            const { data: refreshData } = await supabase.auth.refreshSession();
            if (refreshData.session) {
                accessToken = refreshData.session.access_token;
                response = await fetch(`${API_URL}/${functionName}`, {
                    method: 'POST',
                    headers: buildHeaders(accessToken),
                    body: options.body ? JSON.stringify(options.body) : undefined,
                });
            }
        }

        if (!response.ok) {
            const text = await response.text();
            let errorMsg = `Error calling API (${response.status}): ${text}`;
            try {
                const errorJson = JSON.parse(text);
                if (errorJson.error) errorMsg = errorJson.error;
            } catch (e) {}
            throw new Error(errorMsg);
        }

        const responseData = await response.json();
        return { data: responseData as T, error: null };
    } catch (err: any) {
        console.error(`Error en invokeApiRaw(${functionName}):`, err);
        return { data: null, error: err instanceof Error ? err : new Error(String(err)) };
    }
}

let cachedToken: { token: string; expiresAt: number } | null = null;

/**
 * Get full API headers including a valid Auth token, useful for EventSource / real streaming.
 */
export async function getApiHeaders(): Promise<Record<string, string>> {
    const now = Date.now();
    let accessToken = '';

    if (cachedToken && cachedToken.expiresAt > now + 60000) {
        accessToken = cachedToken.token;
    } else {
        accessToken = await getValidAccessToken() || '';
        if (accessToken) {
            cachedToken = { token: accessToken, expiresAt: now + 30 * 60 * 1000 };
        }
    }

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };

    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }

    return headers;
}
